/**
 * Lake Life Designs — Review Widgets
 * Served from: https://review.lakelifedesigns.com/assets/reviews.js
 * Injected into Shopify theme via script tag in theme.liquid
 *
 * Renders three components:
 *  1. Floating popup (every page, photo-only, relevant reviews)
 *  2. Star badge (product pages, below title)
 *  3. Inline reviews section (product pages, bottom of page)
 */

(function () {
  'use strict';

  // ── Config from Shopify Liquid injection ─────────────────────
  const ctx = window.LLDReviews || {};
  const API = (ctx.apiBase || 'https://review.lakelifedesigns.com') + '/api/reviews';
  const REVIEW_FORM = (ctx.apiBase || 'https://review.lakelifedesigns.com') + '/review';
  const POPUP_DELAY    = (ctx.popupDelay    || 5) * 1000;
  const POPUP_FREQ     = ctx.popupFreq      || 'session';
  const POPUP_MINSTARS = ctx.popupMinStars  || 4;
  const PAGE_TYPE      = ctx.pageType       || 'other';
  const PRODUCT_ID     = ctx.productId      || '';
  const PRODUCT_TITLE  = ctx.productTitle   || '';
  const PRODUCT_URL    = ctx.productUrl     || window.location.href;
  const COLLECTIONS    = (ctx.collections   || []).join(',');
  const TAGS           = (ctx.tags          || []).join(',');
  const PRODUCT_TYPE   = ctx.productType    || '';

  // ── Inject styles ─────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('lld-review-styles')) return;
    const css = `
/* ── LLD Review Widget Styles ── */
.lld-popup {
  position: fixed;
  top: 70vh;
  right: 24px;
  z-index: 99999;
  max-width: 320px;
  width: calc(100vw - 48px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  cursor: pointer;
  transform: translateX(120px);
  opacity: 0;
  transition: transform .4s cubic-bezier(.34,1.56,.64,1), opacity .4s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.lld-popup.lld-visible { transform: translateX(0); opacity: 1; }
.lld-popup__inner { display: flex; align-items: flex-start; gap: 10px; padding: 12px; }
.lld-popup__photo {
  width: 72px; height: 72px; border-radius: 8px; object-fit: cover; flex-shrink: 0;
}
.lld-popup__photo-placeholder {
  width: 72px; height: 72px; border-radius: 8px; background: #F0F4F8;
  display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0;
}
.lld-popup__content { flex: 1; min-width: 0; }
.lld-popup__name { font-weight: 700; font-size: .83rem; color: #1B2B4B; }
.lld-popup__stars { color: #F4A200; font-size: .85rem; letter-spacing: 1px; margin: 2px 0; }
.lld-popup__text {
  font-size: .78rem; color: #5C6B7E; line-height: 1.4;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.lld-popup__product { font-size: .72rem; color: #9AA8B8; margin-top: 4px; }
.lld-popup__product-link {
  color: #2E7DAA; text-decoration: none; font-weight: 600; font-size: .72rem;
}
.lld-popup__product-link:hover { text-decoration: underline; }
.lld-popup__close {
  position: absolute; top: 8px; right: 10px; background: none; border: none;
  font-size: 1rem; color: #9AA8B8; cursor: pointer; padding: 2px 4px; line-height: 1;
  border-radius: 3px;
}
.lld-popup__close:hover { color: #333; background: #f0f0f0; }

/* ── Popup Modal ── */
.lld-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 999999;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
  opacity: 0; transition: opacity .25s;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.lld-modal-overlay.lld-visible { opacity: 1; }
.lld-modal {
  background: #fff; border-radius: 12px; overflow: hidden;
  display: flex; max-width: 720px; width: 100%; max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  transform: scale(.95); transition: transform .25s;
}
.lld-modal-overlay.lld-visible .lld-modal { transform: scale(1); }
.lld-modal__photo {
  width: 45%; flex-shrink: 0; object-fit: cover;
}
.lld-modal__body { padding: 2rem; overflow-y: auto; flex: 1; }
.lld-modal__close {
  position: absolute; top: 12px; right: 14px; background: rgba(255,255,255,.9);
  border: none; border-radius: 50%; width: 32px; height: 32px; font-size: 1rem;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,.15); z-index: 1;
}
.lld-modal__name { font-weight: 700; font-size: 1rem; color: #1B2B4B; margin-bottom: 2px; }
.lld-modal__verified {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: .72rem; color: #2E7DAA; font-weight: 600; margin-bottom: 6px;
}
.lld-modal__stars { color: #F4A200; font-size: 1.1rem; letter-spacing: 2px; margin-bottom: 4px; }
.lld-modal__date { font-size: .75rem; color: #9AA8B8; margin-bottom: 1rem; }
.lld-modal__text { font-size: .9rem; color: #333; line-height: 1.7; margin-bottom: 1.5rem; }
.lld-modal__divider { border: none; border-top: 1px solid #eee; margin: 1rem 0; }
.lld-modal__product-row { display: flex; align-items: center; gap: .75rem; }
.lld-modal__product-thumb { width: 48px; height: 48px; border-radius: 6px; object-fit: cover; }
.lld-modal__product-name { font-size: .82rem; font-weight: 600; color: #1B2B4B; }
.lld-modal__view-btn {
  display: inline-block; margin-top: .5rem; font-size: .78rem; color: #2E7DAA;
  text-decoration: none; border: 1px solid #2E7DAA; border-radius: 4px;
  padding: .3rem .75rem; transition: all .2s;
}
.lld-modal__view-btn:hover { background: #2E7DAA; color: #fff; }

/* ── Star Badge ── */
.lld-star-badge {
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer; text-decoration: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 6px 0 10px;
}
.lld-star-badge__stars { color: #F4A200; font-size: 1rem; letter-spacing: 1px; }
.lld-star-badge__count { font-size: .82rem; color: #5C6B7E; text-decoration: underline; }

/* ── Inline Reviews Section ── */
.lld-reviews-section {
  margin: 3rem auto;
  max-width: 860px;
  padding: 0 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.lld-reviews-section__hd {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem; margin-bottom: 1.5rem;
  padding-bottom: 1rem; border-bottom: 2px solid #F0F4F8;
}
.lld-reviews-section__title { font-size: 1.3rem; font-weight: 700; color: #1B2B4B; }
.lld-write-btn {
  display: inline-block; background: #1B2B4B; color: #fff;
  font-size: .82rem; font-weight: 700; padding: .55rem 1.2rem;
  border-radius: 5px; text-decoration: none; letter-spacing: .03em;
  transition: background .2s;
}
.lld-write-btn:hover { background: #2E7DAA; }
.lld-agg {
  display: flex; align-items: center; gap: 1.5rem;
  flex-wrap: wrap; margin-bottom: 2rem;
}
.lld-agg__big { text-align: center; }
.lld-agg__num { font-size: 3rem; font-weight: 700; color: #1B2B4B; line-height: 1; }
.lld-agg__stars { color: #F4A200; font-size: 1.2rem; }
.lld-agg__count { font-size: .78rem; color: #9AA8B8; margin-top: 2px; }
.lld-bars { flex: 1; min-width: 160px; }
.lld-bar-row {
  display: flex; align-items: center; gap: 8px; margin-bottom: 5px; font-size: .78rem; color: #5C6B7E;
}
.lld-bar-track { flex: 1; height: 8px; background: #F0F4F8; border-radius: 4px; overflow: hidden; }
.lld-bar-fill { height: 100%; background: #F4A200; border-radius: 4px; }
.lld-similar-note { font-size: .78rem; color: #9AA8B8; font-style: italic; margin-bottom: 1rem; }
.lld-review-card {
  padding: 1.25rem 0; border-bottom: 1px solid #F0F4F8;
  display: flex; gap: 1rem;
}
.lld-review-card:last-child { border-bottom: none; }
.lld-review-card__photo {
  width: 80px; height: 80px; border-radius: 8px; object-fit: cover;
  flex-shrink: 0; cursor: pointer; transition: transform .2s;
}
.lld-review-card__photo:hover { transform: scale(1.04); }
.lld-review-card__body { flex: 1; }
.lld-review-card__top { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: .5rem; margin-bottom: 4px; }
.lld-review-card__name { font-weight: 700; font-size: .9rem; color: #1B2B4B; }
.lld-review-card__meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
.lld-review-card__stars { color: #F4A200; font-size: .9rem; }
.lld-review-card__verified { font-size: .7rem; color: #2E7DAA; font-weight: 600; }
.lld-review-card__date { font-size: .72rem; color: #9AA8B8; }
.lld-review-card__variant { font-size: .72rem; color: #9AA8B8; }
.lld-review-card__text { font-size: .88rem; color: #333; line-height: 1.6; }
.lld-review-card__response {
  margin-top: .75rem; background: #F8F9FA; border-radius: 6px;
  padding: .75rem 1rem; border-left: 3px solid #C8973A;
}
.lld-review-card__response-lbl { font-size: .72rem; font-weight: 700; color: #C8973A; text-transform: uppercase; letter-spacing: .05em; margin-bottom: .25rem; }
.lld-review-card__response-text { font-size: .83rem; color: #5C6B7E; line-height: 1.5; }
.lld-pagination { display: flex; gap: .4rem; justify-content: center; padding: 1.5rem 0 0; flex-wrap: wrap; }
.lld-page-btn {
  padding: .4rem .85rem; border-radius: 4px; font-size: .82rem; font-weight: 600;
  cursor: pointer; border: 1.5px solid #DEE3EA; background: #fff; color: #1B2B4B; transition: all .2s;
}
.lld-page-btn:hover { border-color: #2E7DAA; color: #2E7DAA; }
.lld-page-btn.lld-current { background: #1B2B4B; color: #fff; border-color: #1B2B4B; }
@media (max-width: 600px) {
  .lld-modal { flex-direction: column; }
  .lld-modal__photo { width: 100%; height: 220px; }
  .lld-agg { flex-direction: column; gap: .75rem; }
  .lld-review-card__photo { width: 60px; height: 60px; }
}
    `;
    const el = document.createElement('style');
    el.id = 'lld-review-styles';
    el.textContent = css;
    document.head.appendChild(el);
  }

  // ── Helpers ───────────────────────────────────────────────────
  function stars(n) { return '★'.repeat(n) + '☆'.repeat(5 - n); }
  function esc(s)   { return s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : ''; }

  function popupCooldownKey() { return 'lld_popup_shown'; }

  function shouldShowPopup() {
    if (POPUP_FREQ === 'every_visit') return true;
    const key = popupCooldownKey();
    const val = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (!val) return true;
    if (POPUP_FREQ === 'session') return false;
    const ts = parseInt(val, 10);
    const hours = POPUP_FREQ === '24h' ? 24 : (POPUP_FREQ === '7d' ? 168 : 0);
    return Date.now() - ts > hours * 3600000;
  }

  function markPopupShown() {
    const key = popupCooldownKey();
    const ts  = String(Date.now());
    if (POPUP_FREQ === 'session') sessionStorage.setItem(key, ts);
    else localStorage.setItem(key, ts);
  }

  // ── Fetch reviews from API ────────────────────────────────────
  async function fetchReviews(opts = {}) {
    const params = new URLSearchParams();
    if (PRODUCT_ID)   params.set('product_id',   PRODUCT_ID);
    if (COLLECTIONS)  params.set('collections',  COLLECTIONS);
    if (TAGS)         params.set('tags',          TAGS);
    if (PRODUCT_TYPE) params.set('product_type',  PRODUCT_TYPE);
    if (opts.photoOnly) params.set('photo_only', '1');
    if (opts.limit)   params.set('limit',         opts.limit);
    params.set('page_type', PAGE_TYPE);
    try {
      const r = await fetch(API + '?' + params.toString());
      return await r.json();
    } catch(e) { return null; }
  }

  // ── Modal ─────────────────────────────────────────────────────
  function openModal(review) {
    const overlay = document.createElement('div');
    overlay.className = 'lld-modal-overlay';
    overlay.innerHTML = `
      <div class="lld-modal" style="position:relative">
        <button class="lld-modal__close" aria-label="Close">✕</button>
        ${review.photo_url
          ? `<img class="lld-modal__photo" src="${esc(review.photo_url)}" alt="Review photo">`
          : ''}
        <div class="lld-modal__body">
          <div class="lld-modal__name">${esc(review.customer_name)}</div>
          ${review.is_verified ? '<div class="lld-modal__verified">✓ Verified Purchase</div>' : ''}
          <div class="lld-modal__stars">${stars(review.stars)}</div>
          <div class="lld-modal__date">${esc(review.date)}</div>
          <div class="lld-modal__text">${esc(review.review_text)}</div>
          ${review.product_title ? `
          <hr class="lld-modal__divider">
          <div class="lld-modal__product-row">
            <div>
              <div class="lld-modal__product-name">${esc(review.product_title)}</div>
              ${review.product_url ? `<a href="${esc(review.product_url)}" class="lld-modal__view-btn">View product</a>` : ''}
            </div>
          </div>` : ''}
          ${review.public_response ? `
          <div style="margin-top:1rem;background:#f8f9fa;border-radius:6px;padding:.75rem 1rem;border-left:3px solid #C8973A">
            <div style="font-size:.72rem;font-weight:700;color:#C8973A;text-transform:uppercase;letter-spacing:.05em;margin-bottom:.25rem">Response from ${esc(ctx.storeName||'Lake Life Designs')}</div>
            <div style="font-size:.83rem;color:#5C6B7E">${esc(review.public_response)}</div>
          </div>` : ''}
        </div>
      </div>`;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('lld-visible'));

    const close = () => {
      overlay.classList.remove('lld-visible');
      setTimeout(() => overlay.remove(), 300);
    };
    overlay.querySelector('.lld-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function esc(e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
    });
  }

  // ── Popup Widget ──────────────────────────────────────────────
  async function initPopup() {
    const data = await fetchReviews({ photoOnly: true, limit: 50 });
    if (!data || !data.data || !data.data.reviews.length) return;

    // Filter to qualifying reviews with photos
    const allReviews = data.data.reviews.filter(r => r.stars >= POPUP_MINSTARS && r.photo_url);
    if (!allReviews.length) return;

    // Sort: exact product match first, then product type, then rest
    // This ensures Lake Nolan reviews show first on Lake Nolan pages
    const sorted = [
      ...allReviews.filter(r => r.product_url && PRODUCT_URL && r.product_url === PRODUCT_URL),
      ...allReviews.filter(r => !(r.product_url && PRODUCT_URL && r.product_url === PRODUCT_URL)),
    ];

    // Shuffle each tier independently so it's not always the same review
    function shuffle(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    }

    const exactMatches = shuffle(allReviews.filter(r =>
      r.product_url && PRODUCT_URL && r.product_url === PRODUCT_URL
    ));
    const otherReviews = shuffle(allReviews.filter(r =>
      !(r.product_url && PRODUCT_URL && r.product_url === PRODUCT_URL)
    ));
    const queue = [...exactMatches, ...otherReviews];

    let queueIndex = 0;
    let cycleTimer  = null;

    function showPopup(review) {
      // Remove any existing popup
      const existing = document.getElementById('lld-active-popup');
      if (existing) existing.remove();

      const popup = document.createElement('div');
      popup.className = 'lld-popup';
      popup.id = 'lld-active-popup';
      popup.setAttribute('role', 'complementary');
      popup.setAttribute('aria-label', 'Customer Review');
      popup.innerHTML = `
        <button class="lld-popup__close" aria-label="Close review popup">✕</button>
        <div class="lld-popup__inner">
          ${review.photo_url
            ? `<img class="lld-popup__photo" src="${esc(review.photo_url)}" alt="Review photo" loading="lazy">`
            : `<div class="lld-popup__photo-placeholder">📷</div>`}
          <div class="lld-popup__content">
            <div class="lld-popup__name">${esc(review.customer_name)}</div>
            <div class="lld-popup__stars">${stars(review.stars)}</div>
            <div class="lld-popup__text">${esc(review.review_text)}</div>
            ${review.product_title
              ? `<div class="lld-popup__product">
                  ${review.product_url
                    ? `<a href="${esc(review.product_url)}" class="lld-popup__product-link"
                         onclick="event.stopPropagation()">${esc(review.product_title)} →</a>`
                    : esc(review.product_title)}
                 </div>`
              : ''}
          </div>
        </div>`;

      document.body.appendChild(popup);

      // Slide in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => popup.classList.add('lld-visible'));
      });

      // Close button -- dismiss and schedule next
      popup.querySelector('.lld-popup__close').addEventListener('click', e => {
        e.stopPropagation();
        dismissPopup(popup, true);
      });

      // Click body to expand modal
      popup.querySelector('.lld-popup__inner').addEventListener('click', () => {
        openModal(review);
      });
    }

    function dismissPopup(popup, scheduleNext) {
      if (cycleTimer) { clearTimeout(cycleTimer); cycleTimer = null; }
      popup.classList.remove('lld-visible');
      setTimeout(() => {
        popup.remove();
        if (scheduleNext && queue.length > 1) {
          // Advance to next review in queue, cycling back to start
          queueIndex = (queueIndex + 1) % queue.length;
          cycleTimer = setTimeout(() => showPopup(queue[queueIndex]), 10000);
        }
      }, 400);
    }

    // Initial show after page-load delay
    markPopupShown();
    cycleTimer = setTimeout(() => showPopup(queue[0]), POPUP_DELAY);
  }

  // ── Star Badge ────────────────────────────────────────────────
  async function initStarBadge() {
    const target = document.getElementById('lld-star-badge');
    if (!target) return;

    const data = await fetchReviews({ limit: 1 });
    if (!data || !data.data) return;

    const agg = data.data.aggregate;
    if (!agg.total) return;

    const avg = parseFloat(agg.average);
    const badge = document.createElement('a');
    badge.className = 'lld-star-badge';
    badge.href = '#lld-reviews-section';

    // Visual stars
    let starHtml = '';
    for (let i = 1; i <= 5; i++) {
      if (avg >= i) starHtml += '★';
      else if (avg >= i - 0.5) starHtml += '⯨';
      else starHtml += '☆';
    }

    badge.innerHTML = `
      <span class="lld-star-badge__stars">${starHtml}</span>
      <span class="lld-star-badge__count">${agg.average} (${agg.total} review${agg.total !== 1 ? 's' : ''})</span>`;

    badge.addEventListener('click', e => {
      e.preventDefault();
      const section = document.getElementById('lld-reviews-section');
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    target.appendChild(badge);
  }

  // ── Inline Reviews Section ────────────────────────────────────
  async function initReviewsSection() {
    const target = document.getElementById('lld-reviews-section');
    if (!target) return;

    const data = await fetchReviews({ limit: 50 });
    if (!data || !data.data) return;

    const { reviews, aggregate } = data.data;
    const source = data.source;

    const isSimilar = source !== 'exact';
    const PER_PAGE  = 5;
    let currentPage = 1;

    function buildReviewFormUrl() {
      const p = new URLSearchParams();
      if (PRODUCT_ID)    p.set('pid',         PRODUCT_ID);
      if (PRODUCT_TITLE) p.set('ptitle',       PRODUCT_TITLE);
      if (PRODUCT_URL)   p.set('purl',         PRODUCT_URL);
      if (COLLECTIONS)   p.set('collections',  COLLECTIONS);
      if (TAGS)          p.set('tags',          TAGS);
      return REVIEW_FORM + '?' + p.toString();
    }

    function renderBar(pct) {
      return `<div class="lld-bar-track"><div class="lld-bar-fill" style="width:${pct}%"></div></div>`;
    }

    function renderCard(r) {
      return `<div class="lld-review-card">
        ${r.photo_url ? `<img class="lld-review-card__photo" src="${esc(r.photo_url)}" alt="Review photo" loading="lazy" data-review='${JSON.stringify({...r, review_text: r.review_text, customer_name: r.customer_name}).replace(/'/g,"&#39;")}'>` : ''}
        <div class="lld-review-card__body">
          <div class="lld-review-card__top">
            <div class="lld-review-card__name">${esc(r.customer_name)}</div>
            <div class="lld-review-card__date">${esc(r.date)}</div>
          </div>
          <div class="lld-review-card__meta">
            <span class="lld-review-card__stars">${'★'.repeat(r.stars)}</span>
            ${r.is_verified ? '<span class="lld-review-card__verified">✓ Verified Purchase</span>' : ''}
            ${r.variant_info ? `<span class="lld-review-card__variant">${esc(r.variant_info)}</span>` : ''}
          </div>
          <div class="lld-review-card__text">${esc(r.review_text)}</div>
          ${r.public_response ? `
          <div class="lld-review-card__response">
            <div class="lld-review-card__response-lbl">Response from ${esc(ctx.storeName||'Lake Life Designs')}</div>
            <div class="lld-review-card__response-text">${esc(r.public_response)}</div>
          </div>` : ''}
        </div>
      </div>`;
    }

    function renderPage(page) {
      const start = (page - 1) * PER_PAGE;
      const slice = reviews.slice(start, start + PER_PAGE);
      const total = reviews.length;
      const pages = Math.ceil(total / PER_PAGE);

      let pager = '';
      if (pages > 1) {
        pager = '<div class="lld-pagination">';
        if (page > 1) pager += `<button class="lld-page-btn" data-page="${page-1}">‹ Prev</button>`;
        for (let i = Math.max(1, page-2); i <= Math.min(pages, page+2); i++) {
          pager += `<button class="lld-page-btn ${i===page?'lld-current':''}" data-page="${i}">${i}</button>`;
        }
        if (page < pages) pager += `<button class="lld-page-btn" data-page="${page+1}">Next ›</button>`;
        pager += '</div>';
      }

      const listEl = target.querySelector('.lld-review-list');
      const pagerEl = target.querySelector('.lld-pager');
      if (listEl) listEl.innerHTML = slice.map(renderCard).join('');
      if (pagerEl) pagerEl.innerHTML = pager;

      // Attach photo click handlers
      target.querySelectorAll('.lld-review-card__photo').forEach(img => {
        img.addEventListener('click', () => {
          try {
            const r = JSON.parse(img.getAttribute('data-review').replace(/&#39;/g,"'"));
            openModal(r);
          } catch(e) {}
        });
      });

      // Attach pager click handlers
      target.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', () => {
          currentPage = parseInt(btn.getAttribute('data-page'), 10);
          renderPage(currentPage);
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    const total  = aggregate.total;
    const avg    = aggregate.average;
    const dist   = aggregate.distribution;
    const maxBar = Math.max(...Object.values(dist), 1);

    const avgDisplay = parseFloat(avg).toFixed(1);
    let starDisplay = '';
    for (let i = 1; i <= 5; i++) {
      if (avg >= i) starDisplay += '★';
      else if (avg >= i - 0.5) starDisplay += '⯨';
      else starDisplay += '☆';
    }

    target.innerHTML = `
      <div class="lld-reviews-section" id="lld-reviews-section-inner">
        <div class="lld-reviews-section__hd">
          <div class="lld-reviews-section__title">Reviews ${total ? '('+total+')' : ''}</div>
          <a href="${buildReviewFormUrl()}" class="lld-write-btn" target="_blank" rel="noopener">Write a Review</a>
        </div>

        ${total ? `
        <div class="lld-agg">
          <div class="lld-agg__big">
            <div class="lld-agg__num">${avgDisplay}</div>
            <div class="lld-agg__stars">${starDisplay}</div>
            <div class="lld-agg__count">${total} review${total !== 1 ? 's' : ''}</div>
          </div>
          <div class="lld-bars">
            ${[5,4,3,2,1].map(n => `
            <div class="lld-bar-row">
              <span>${n}★</span>
              ${renderBar(Math.round((dist[n]||0)/maxBar*100))}
              <span>${dist[n]||0}</span>
            </div>`).join('')}
          </div>
        </div>` : ''}

        ${isSimilar && reviews.length ? `
        <div class="lld-similar-note">Includes reviews from similar products</div>` : ''}

        ${!reviews.length ? '<p style="color:#9AA8B8;padding:1rem 0;font-size:.9rem">No reviews yet. Be the first to leave one!</p>' : ''}

        <div class="lld-review-list"></div>
        <div class="lld-pager"></div>
      </div>`;

    if (reviews.length) renderPage(1);
  }

  // ── Init ──────────────────────────────────────────────────────
  function init() {
    injectStyles();
    initPopup();
    if (PAGE_TYPE === 'product') {
      initStarBadge();
      initReviewsSection();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
